import React, { useState, useMemo, useEffect, useCallback } from "react";
import { InputField } from "../../../../components/controls/InputField";
import { treeViewUserSelect } from "../../../contentLibrary/utils/componentHelpers";
import "./index.scss";
import { CheckTreePicker } from "rsuite";
import _ from "lodash";

export default function InputFieldTreePicker({
  defaultSelectedIds,
  setSelectedUserIds,
  selectedUserIds,
  appUserMgr,
  userData,
}) {
  const [loading, setLoading] = useState(false);
  const [loadedIds, setLoadedIds] = useState([]);

  const mapToTreeNode = useCallback((item) => {
    let itemValue = item.OrgId;

    switch (item.NodeType) {
      case "District":
        itemValue = item.DistrictId;
        break;
      case "School":
        itemValue = item.SchoolId;
        break;
      case "User":
        itemValue = item.UserId;
        break;
      default:
        break;
    }

    if (
      item.NodeType === "User" &&
      item.DistrictId === null &&
      item.SchoolId === null
    ) {
      return {
        value: item.OrgId + "," + itemValue,
        label: item.Name,
        OrgId: item.OrgId,
        DistrictId: item.DistrictId,
        SchoolId: item.SchoolId,
        UserId: item.UserId,
        NodeType: item.NodeType,
        children: null,
      };
    } else if (item.NodeType === "User" && item.SchoolId === null) {
      return {
        value: item.DistrictId + "," + itemValue,
        label: item.Name,
        OrgId: item.OrgId,
        DistrictId: item.DistrictId,
        SchoolId: item.SchoolId,
        UserId: item.UserId,
        NodeType: item.NodeType,
        children: null,
      };
    } else if (item.NodeType === "User" && item.SchoolId === null) {
      return {
        value: item.DistrictId + "," + itemValue,
        label: item.Name,
        OrgId: item.OrgId,
        DistrictId: item.DistrictId,
        SchoolId: item.SchoolId,
        UserId: item.UserId,
        NodeType: item.NodeType,
        children: null,
      };
    } else if (item.NodeType === "User") {
      return {
        value: itemValue,
        label: item.Name,
        OrgId: item.OrgId,
        DistrictId: item.DistrictId,
        SchoolId: item.SchoolId,
        UserId: item.UserId,
        NodeType: item.NodeType,
        children: null,
      };
    }

    return {
      value: itemValue,
      label: item.Name,
      OrgId: item.OrgId,
      DistrictId: item.DistrictId,
      SchoolId: item.SchoolId,
      NodeType: item.NodeType,
      children:
        item.Children?.length > 0 ? item.Children.map(mapToTreeNode) : [],
    };
  }, []);

  const convertToTreeNodes = useCallback(
    (newUserData) => newUserData?.map(mapToTreeNode) ?? [],
    [mapToTreeNode]
  );

  const [treeData, setTreeData] = useState(convertToTreeNodes(userData));

  const memoizedTreeData = useMemo(() => treeData, [treeData]);

  const onTreeSelect = useCallback(
    (activeNode, vals, event) => {
      if (activeNode?.value?.includes(","))
        activeNode.value = activeNode.value.split(",")[1];

      treeViewUserSelect(activeNode, vals, setSelectedUserIds, selectedUserIds);
    },
    [setSelectedUserIds, selectedUserIds]
  );

  const hasUserType = useCallback((node) => {
    if (!node.Children) return false;
    return node.Children.some(
      (child) => child.NodeType === "User" || hasUserType(child)
    );
  }, []);

  const updateEntity = useCallback((tree1, tree2) => {
    const mergeChildren = (node1, node2) => {
      node2?.children?.forEach((child2) => {
        const existingChild = node1?.children?.find(
          (child1) => child1.value === child2.value
        );

        if (!existingChild) {
          node1?.children?.push(child2);
        } else {
          mergeChildren(existingChild, child2);
        }
      });

      node1.children = node1.children?.sort((a, b) => {
        if (a.NodeType === "User" && b.NodeType !== "User") return 1;
        if (a.NodeType !== "User" && b.NodeType === "User") return -1;
        return a.label.localeCompare(b.label);
      });

      if (node1.NodeType === "User") {
        node1.children = null;
      }
    };

    mergeChildren(tree1, tree2);
    return tree1;
  }, []);

  const updateUserData = useCallback(
    (entity, level) => {
      const newEntity = mapToTreeNode(entity);
      const newUserData = memoizedTreeData.find(
        (f) => f.OrgId === newEntity.OrgId
      );

      if (!newUserData) {
        setLoading(false);
        return;
      }

      switch (level) {
        case "org":
          setLoadedIds([...loadedIds, newEntity.value]);
          newUserData.children = newEntity.children;
          break;

        case "district":
          if (newEntity.children[0]?.children?.length > 0) {
            const districtToUpdate = newUserData.children.find(
              (child) => child.value === newEntity?.children[0].value
            );

            if (districtToUpdate) {
              setLoadedIds([...loadedIds, districtToUpdate.value]);
              districtToUpdate.children = newEntity.children[0].children;
            }
          }
          break;

        case "school":
          if (newEntity?.children[0]?.children[0]?.children?.length > 0) {
            const districtToUpdate = newUserData.children.find(
              (child) => child.value === newEntity.children[0].value
            );
            if (districtToUpdate) {
              const schoolToUpdate = districtToUpdate.children.find(
                (child) =>
                  child.value === newEntity.children[0].children[0].value
              );
              if (schoolToUpdate) {
                setLoadedIds([...loadedIds, schoolToUpdate.value]);
                schoolToUpdate.children =
                  newEntity.children[0].children[0].children;
              }
            }
          }
          break;
      }

      const updatedTreeData = memoizedTreeData.map((data) =>
        data.OrgId === newEntity.OrgId ? newUserData : data
      );

      setTreeData(updatedTreeData);
      setLoading(false);
    },
    [mapToTreeNode, memoizedTreeData, updateEntity]
  );

  const onTreeExpand = useCallback(
    _.debounce(async (expandedNodes, activeNode) => {
      if (
        expandedNodes.includes(activeNode.value) &&
        !loadedIds.some((id) => id === activeNode.value)
      ) {
        if (!hasUserType(activeNode)) {
          if (activeNode.NodeType === "Org") {
            setLoading(true);
            const orgNew = await appUserMgr?.GetSubTree(activeNode.OrgId);
            if (orgNew) {
              updateUserData(orgNew[0], "org");
            }
          } else if (activeNode.NodeType === "District") {
            setLoading(true);
            const distNew = await appUserMgr?.GetSubTree(
              activeNode.OrgId,
              activeNode.DistrictId
            );

            if (distNew) {
              if (distNew[0]?.Children?.length > 0) {
                updateUserData(distNew[0], "district");
              } else {
                setLoading(false);
              }
            }
          } else if (activeNode.NodeType === "School") {
            setLoading(true);
            const schNew = await appUserMgr?.GetSubTree(
              activeNode.OrgId,
              activeNode.DistrictId,
              activeNode.SchoolId
            );
            if (schNew) {
              updateUserData(schNew[0], "school");
            }
          }
        }
      }
    }, 300),
    [appUserMgr, hasUserType, updateUserData]
  );

  return (
    <InputField>
      <CheckTreePicker
        data={memoizedTreeData ?? []}
        disabledItemValues={defaultSelectedIds ?? []}
        defaultValue={defaultSelectedIds ?? []}
        onSelect={onTreeSelect}
        onExpand={(expanded, node) => {
          onTreeExpand(expanded, node);
        }}
        searchable
        cascade={false}
        loading={loading}
        placement="top"
        className="fixed-tree-picker"
        virtualized={false}
      />
    </InputField>
  );
}
